import {
  SponsorRequestActions,
  SponsorRequestActionsModals,
} from 'app/components/shared/SponsorRequestActionsModals'
import { Button } from 'app/components/ui/Button'
import { Card, CardBgGradients } from 'app/components/ui/Card'
import { Text } from 'app/components/ui/Text'
import { protectedRoutes } from 'app/routing/routes'
import { formatDateShort } from 'app/utils/dateUtils'
import { SponsorRequest, SponsorRequestStatus } from 'EstraSponsor'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

type Props = {
  sponsorRequest: SponsorRequest
}

export const sponsorRequestStatusBgColor: {
  [key in SponsorRequestStatus]: CardBgGradients
} = {
  draft: 'orange_yellow',
  new: 'blue',
  rejected: 'red',
  in_progress: 'orange_yellow',
  approved_with_changes: 'orange_yellow',
  approved: 'green',
  finalized_to_sign: 'orange_yellow',
  finalized_signed: 'orange_yellow',
  finalized_canceled: 'red',
  finalized: 'green',
  canceled: 'red',
  expired: 'red',
}

export const SponsorRequestBox = ({ sponsorRequest }: Props): JSX.Element => {
  const { push } = useHistory()
  const [action, setAction] = useState<SponsorRequestActions | null>(null)

  return (
    <>
      <div style={{ gridArea: 'Status' }}>
        <Card
          shadow
          padding={'2rem'}
          bgGradient={sponsorRequestStatusBgColor[sponsorRequest.status]}
        >
          {sponsorRequest.status === 'draft' &&
          !sponsorRequest.ready_to_be_sent ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione si trova al momento in bozza,
              completa tutti i dati per procedere con l&apos;invio.
            </Text>
          ) : sponsorRequest.status === 'draft' &&
            sponsorRequest.ready_to_be_sent ? (
            <>
              <Text color='white' variant='s18'>
                La tua richiesta di sponsorizzazione si trova al momento in
                bozza, puoi procedere con l&apos;invio cliccando il bottone qua
                sotto.
              </Text>
              <CardButtonsWrapper>
                <Button onClick={() => setAction('SEND')}>
                  Invia la richiesta
                </Button>
              </CardButtonsWrapper>
            </>
          ) : sponsorRequest.status === 'new' ? (
            <Text color='white' variant='s18'>
              Grazie per avere inviato la tua richiesta di sponsorizzazione in
              data {formatDateShort(sponsorRequest.sent_at as string)}.{'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
              {'\n'}
              {'\n'}
              Riceverai una notifica via email non appena avremo visionato il
              documento
            </Text>
          ) : //
          sponsorRequest.status === 'rejected' ||
            sponsorRequest.status === 'canceled' ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione è stata rifiutata.
              {'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
              {'\n'}
              {'\n'}
              {sponsorRequest.outcome_document_url && (
                <DocumentLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href={sponsorRequest.outcome_document_url}
                >
                  Vedi il documento di rifiuto
                </DocumentLink>
              )}
            </Text>
          ) : sponsorRequest.status === 'finalized_canceled' ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione è stata cancellata.
              {'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
              {'\n'}
              {'\n'}
              {sponsorRequest.signed_outcome_document_url && (
                <>
                  <DocumentLink
                    target='_blank'
                    rel='noopener noreferrer'
                    href={sponsorRequest.signed_outcome_document_url || '#'}
                  >
                    Vedi il documento
                  </DocumentLink>
                  {'\n'}
                  {'\n'}
                </>
              )}
            </Text>
          ) : sponsorRequest.status === 'in_progress' ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione inviata in data{' '}
              {formatDateShort(sponsorRequest.sent_at as string)} è in gestione
              alla nostra amministrazione.
              {'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
              {'\n'}
              {'\n'}
              Riceverai una notifica via email al completamento.
            </Text>
          ) : sponsorRequest.status === 'approved_with_changes' ? (
            <>
              <Text color='white' variant='s18'>
                La tua richiesta di sponsorizzazione è stata approvata con delle
                modifiche proposte.
                {'\n'}
                {'\n'}
                <DocumentLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href={sponsorRequest.sponsor_request_pdf_url || '#'}
                >
                  Vedi la tua richiesta
                </DocumentLink>
                {'\n'}
                {'\n'}
                Cliccando il bottone qua sotto puoi decidere se accettare o
                rifiutare le modifiche che ti abbiamo proposto.
              </Text>
              {sponsorRequest.expires_at && (
                <Text color='white' variant='s18'>
                  <b>Hai tempo fino al:</b>{' '}
                  {formatDateShort(sponsorRequest.expires_at as string)}
                </Text>
              )}
              {'\n'}
              <Text color='white' variant='s18'>
                Una volta scaduta, devi sottoporre una nuova richiesta.
              </Text>
              <CardButtonsWrapper>
                <Button onClick={() => setAction('EVALUATE_CHANGES')}>
                  Visualizza modifiche
                </Button>
              </CardButtonsWrapper>
            </>
          ) : sponsorRequest.status === 'approved' ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione è stata approvata, si
              procederà ora con la fase finale.
              {'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
            </Text>
          ) : sponsorRequest.status === 'finalized_to_sign' ? (
            <>
              <Text color='white' variant='s18'>
                La tua richiesta di sponsorizzazione è stata approvata, scarica
                e firma il documento per completare la richiesta.
                {'\n'}
                {'\n'}
                <DocumentLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href={sponsorRequest.sponsor_request_pdf_url || '#'}
                >
                  Vedi la tua richiesta
                </DocumentLink>
              </Text>
              <CardButtonsWrapper>
                <Button onClick={() => setAction('SIGN')}>
                  Firma il documento
                </Button>
              </CardButtonsWrapper>
            </>
          ) : sponsorRequest.status === 'finalized_signed' ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione inviata in data{' '}
              {formatDateShort(sponsorRequest.sent_at as string)} è in gestione
              alla nostra amministrazione.{'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
              {'\n'}
              {'\n'}
              {sponsorRequest.outcome_document_url && (
                <>
                  <DocumentLink
                    target='_blank'
                    rel='noopener noreferrer'
                    href={sponsorRequest.signed_outcome_document_url || '#'}
                  >
                    Vedi il documento
                  </DocumentLink>
                  {'\n'}
                  {'\n'}
                </>
              )}
              Riceverai una notifica via email al completamento.
            </Text>
          ) : sponsorRequest.status === 'finalized' ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione inviata in data{' '}
              {formatDateShort(sponsorRequest.sent_at as string)} è stata
              completata.
              {'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
              {'\n'}
              {'\n'}
              {(sponsorRequest.signed_outcome_document_url ||
                sponsorRequest.outcome_document_url) && (
                <>
                  <DocumentLink
                    target='_blank'
                    rel='noopener noreferrer'
                    href={
                      sponsorRequest.signed_outcome_document_url ||
                      sponsorRequest.outcome_document_url ||
                      '#'
                    }
                  >
                    Vedi il documento
                  </DocumentLink>
                </>
              )}
            </Text>
          ) : sponsorRequest.status === 'expired' ? (
            <Text color='white' variant='s18'>
              La tua richiesta di sponsorizzazione è scaduta in data{' '}
              {formatDateShort(sponsorRequest.expires_at as string)} {'\n'}
              {'\n'}
              <DocumentLink
                target='_blank'
                rel='noopener noreferrer'
                href={sponsorRequest.sponsor_request_pdf_url || '#'}
              >
                Vedi la tua richiesta
              </DocumentLink>
            </Text>
          ) : null}
        </Card>

        {/* Actions outside the Card */}
        {sponsorRequest.status === 'draft' ? (
          <ActionsWrapper>
            <Text variant='s12' isItalic mb={0.5}>
              Se vuoi, puoi modificare o eliminare la tua richiesta.
            </Text>
            <ActionsButtonsWrapper>
              <Button
                onClick={() =>
                  push(
                    protectedRoutes.sponsorRequestEdit.makeUrl(
                      sponsorRequest.id
                    )
                  )
                }
              >
                Modifica
              </Button>
              <Button onClick={() => setAction('DELETE')}>Elimina</Button>
            </ActionsButtonsWrapper>
          </ActionsWrapper>
        ) : sponsorRequest.status === 'finalized_signed' ? (
          <ActionsWrapper>
            <Text variant='s12' isItalic mb={0.5}>
              Se vuoi, puoi sostituire il documento caricato in precedenza.
            </Text>
            <ActionsButtonsWrapper>
              <Button onClick={() => setAction('SIGN')}>
                Sostituisci documento
              </Button>
            </ActionsButtonsWrapper>
          </ActionsWrapper>
        ) : null}

        {action && (
          <SponsorRequestActionsModals
            action={action}
            sponsorRequest={sponsorRequest}
            onCloseRequest={() => setAction(null)}
          />
        )}
      </div>
    </>
  )
}

const DocumentLink = styled.a`
  color: white;
  text-decoration: underline;
`

const CardButtonsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  margin-top: 2rem;
`

const ActionsWrapper = styled.div`
  margin-top: 1rem;
`

const ActionsButtonsWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`
